import React, { useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';

const ADMIN_API = '/api/admin';

function formatDate(value) {
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return '—';
  return d.toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' });
}

export default function AdminSubscribers() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [query, setQuery] = useState('');
  const [filter, setFilter] = useState('active');
  const [saving, setSaving] = useState(null);

  const load = async () => {
    setLoading(true);
    setError(null);
    try {
      const { data } = await axios.get(`${ADMIN_API}/newsletter/subscribers`);
      setItems(data);
    } catch (e) {
      setError(
        e.response?.status === 401
          ? 'Not authorised. Reload the page and sign in again.'
          : 'Could not load subscribers. Is the server running?'
      );
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const setStatus = async (id, status) => {
    setSaving(id);
    const previous = items;
    setItems((cur) => cur.map((i) => (i.id === id ? { ...i, status } : i)));
    try {
      await axios.patch(`${ADMIN_API}/newsletter/subscribers/${id}`, { status });
      toast.success(status === 'unsubscribed' ? 'Marked unsubscribed' : 'Marked active');
    } catch {
      setItems(previous);
      toast.error('Could not save that change');
    } finally {
      setSaving(null);
    }
  };

  const visible = useMemo(() => {
    const q = query.trim().toLowerCase();
    return items.filter((i) => {
      if (filter !== 'all' && i.status !== filter) return false;
      return !q || i.email.toLowerCase().includes(q);
    });
  }, [items, query, filter]);

  const counts = useMemo(() => {
    const c = { all: items.length, active: 0, unsubscribed: 0 };
    items.forEach((i) => { if (c[i.status] !== undefined) c[i.status] += 1; });
    return c;
  }, [items]);

  // Only ever the currently visible set. With the default "active" filter that
  // means opted-out people cannot be copied into a send by accident.
  const copyEmails = async () => {
    const list = visible.map((i) => i.email).join(', ');
    try {
      await navigator.clipboard.writeText(list);
      toast.success(`Copied ${visible.length} address${visible.length === 1 ? '' : 'es'} — paste into BCC`);
    } catch {
      toast.error('Could not access the clipboard');
    }
  };

  // Includes the per-person unsubscribe link so the CSV can drive a mail merge
  // in Gmail or Sheets - that is what lets a recipient unsubscribe in one click
  // without typing their address.
  // For sending to one person by hand: their link, straight to the clipboard.
  const copyLink = async (row) => {
    if (!row.unsubscribe_url) {
      toast.error('No link for this subscriber yet');
      return;
    }
    try {
      await navigator.clipboard.writeText(row.unsubscribe_url);
      toast.success(`Unsubscribe link copied for ${row.email}`);
    } catch {
      toast.error('Could not access the clipboard');
    }
  };

  const download = () => {
    const esc = (v) => `"${String(v ?? '').replace(/"/g, '""')}"`;
    const csv = ['email,status,subscribed_at,unsubscribe_url',
      ...visible.map((i) => [i.email, i.status, i.subscribed_at, i.unsubscribe_url].map(esc).join(','))].join('\n');
    const url = URL.createObjectURL(new Blob([csv], { type: 'text/csv;charset=utf-8;' }));
    const a = document.createElement('a');
    a.href = url;
    a.download = `orboroi-subscribers-${new Date().toISOString().slice(0, 10)}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  return (
    <div>
      <div className="mb-6 flex flex-wrap items-end justify-between gap-4">
        <div>
          <h1 className="text-2xl font-semibold text-slate-900">Newsletter subscribers</h1>
          <p className="mt-1 text-sm text-slate-500">
            Everyone who has signed up through the website footer.
          </p>
        </div>
        <div className="flex gap-2">
          <button onClick={load} className="rounded-md border border-slate-300 bg-white px-3 py-2 text-sm font-medium text-slate-700 hover:bg-slate-50">
            Refresh
          </button>
          <button onClick={copyEmails} disabled={!visible.length}
            className="rounded-md border border-slate-300 bg-white px-3 py-2 text-sm font-medium text-slate-700 hover:bg-slate-50 disabled:opacity-40">
            Copy emails
          </button>
          <button onClick={download} disabled={!visible.length}
            className="rounded-md bg-[#379F7F] px-3 py-2 text-sm font-medium text-white hover:bg-[#2b7d64] disabled:opacity-40">
            Download CSV
          </button>
        </div>
      </div>

      <div className="mb-4 flex flex-wrap items-center gap-2">
        {['active', 'unsubscribed', 'all'].map((s) => (
          <button key={s} onClick={() => setFilter(s)}
            className={`rounded-full px-3 py-1.5 text-sm capitalize ring-1 transition ${
              filter === s ? 'bg-slate-900 text-white ring-slate-900'
                           : 'bg-white text-slate-600 ring-slate-200 hover:bg-slate-100'}`}>
            {s} <span className="opacity-60">{counts[s] ?? 0}</span>
          </button>
        ))}
        <input value={query} onChange={(e) => setQuery(e.target.value)} placeholder="Search email…"
          className="ml-auto w-full min-w-[14rem] max-w-xs rounded-md border border-slate-300 px-3 py-2 text-sm focus:border-[#379F7F] focus:outline-none focus:ring-1 focus:ring-[#379F7F]" />
      </div>

      {/* Sits directly above "Copy emails" on purpose: this is the moment the
          mistake gets made, and pasting into To instead of BCC cannot be undone. */}
      {/* Sits directly above the copy button on purpose: this is the moment the
          mistake gets made, and pasting subscribers into To instead of BCC
          cannot be undone. */}
      <div className="mb-4 space-y-2 rounded-md border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-800">
        <p>
          <strong>Sending to everyone at once (BCC):</strong> paste into{' '}
          <strong>BCC</strong>, never To or CC, and include this line in the email:{' '}
          <code className="rounded bg-white/70 px-1.5 py-0.5 text-[13px] text-amber-900">
            To unsubscribe: https://orboroi.com/unsubscribe
          </code>
        </p>
        <p>
          <strong>Sending to one person:</strong> use <strong>Copy link</strong> on their row
          and paste it into the email — they unsubscribe in one click, with nothing to type.
          For several people, the CSV has an{' '}
          <code className="rounded bg-white/70 px-1.5 py-0.5 text-[13px] text-amber-900">unsubscribe_url</code>{' '}
          column for a mail merge.
        </p>
      </div>

      {loading && <p className="py-16 text-center text-sm text-slate-500">Loading…</p>}

      {error && !loading && (
        <div className="rounded-md border border-red-200 bg-red-50 p-4 text-sm text-red-700">{error}</div>
      )}

      {!loading && !error && !visible.length && (
        <div className="rounded-lg border border-dashed border-slate-300 bg-white py-16 text-center">
          <p className="text-sm text-slate-500">
            {items.length ? 'Nothing matches that filter.' : 'No subscribers yet.'}
          </p>
        </div>
      )}

      {!loading && !error && !!visible.length && (
        <div className="overflow-hidden rounded-lg border border-slate-200 bg-white shadow-sm">
          <table className="w-full text-left text-sm">
            <thead className="border-b border-slate-200 bg-slate-50 text-xs uppercase tracking-wide text-slate-500">
              <tr>
                <th className="px-4 py-3 font-medium">Subscribed</th>
                <th className="px-4 py-3 font-medium">Email</th>
                <th className="px-4 py-3 font-medium">Status</th>
                <th className="px-4 py-3 font-medium"></th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {visible.map((i) => (
                <tr key={i.id} className="hover:bg-slate-50/60">
                  <td className="whitespace-nowrap px-4 py-3 text-slate-500">{formatDate(i.subscribed_at)}</td>
                  <td className="px-4 py-3">
                    <a href={`mailto:${i.email}`} className="text-[#379F7F] hover:underline">{i.email}</a>
                  </td>
                  <td className="px-4 py-3">
                    <span className={`inline-block rounded-full px-2 py-0.5 text-xs font-medium capitalize ring-1 ${
                      i.status === 'active'
                        ? 'bg-[#379F7F]/10 text-[#2b7d64] ring-[#379F7F]/30'
                        : 'bg-slate-100 text-slate-500 ring-slate-200'}`}>
                      {i.status}
                    </span>
                  </td>
                  <td className="px-4 py-3">
                    <div className="flex justify-end gap-1">
                      <button
                        onClick={() => copyLink(i)}
                        title="Copy this person's one-click unsubscribe link, for sending to them individually"
                        className="rounded border border-slate-200 px-2 py-1 text-xs text-slate-500 hover:bg-slate-100">
                        Copy link
                      </button>
                      <button
                        disabled={saving === i.id}
                        onClick={() => setStatus(i.id, i.status === 'active' ? 'unsubscribed' : 'active')}
                        className="rounded border border-slate-200 px-2 py-1 text-xs text-slate-500 hover:bg-slate-100 disabled:opacity-40">
                        {i.status === 'active' ? 'Mark unsubscribed' : 'Reactivate'}
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <p className="mt-4 text-xs text-slate-400">
        Showing {visible.length} of {items.length}. Exported files contain personal data — handle accordingly.
      </p>
    </div>
  );
}
